import { BulkChangeData, BulkEntity, BulkMatches, BulkSearchResult } from "@models/ui-models/bulk-change-data";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";

export function transformBulkSearchData(res: BulkSearchResult): BulkChangeData[] {
  const transformEntity = (entity: BulkEntity, identifier: string) => {
    return {
      name: entity.name,
      [identifier]: entity.entityNumber,
      dateOfEstablishment: entity.dateOfEstablishment ?? '',
      entityId: entity.entityId,
      matches: transformRelationships(entity)
    };
  };

  const transformRelationships = (entity: BulkEntity) => {
    const transformRegisteredAddress: BulkMatches[] = [];
    if (entity.registeredAddress) {
      transformRegisteredAddress.push({
        label: 'Registered Address',
        value: false,
        address: entity.registeredAddress.normalizedFullAddress,
        relationshipsId: []
      });
    }

    const transformPrincipalAddress: BulkMatches[] = [];
    if (entity.principalAddress) {
      transformPrincipalAddress.push({
        label: 'Principal Place of Business',
        value: false,
        address: entity.principalAddress.normalizedFullAddress,
        relationshipsId: []
      });
    }

    const denyMinutesDocumentType = entity.relationships.filter((relationship) => relationship.type == RelationshipType.Director).length < 2;
    const relationShips = entity.relationships?.reduce((acc, r) => {
      const existing = acc.find(item => item.label === (r.individualDataOverride?.fullName ?? ''));
      if (existing) {
        existing.types?.push(RelationshipTypeLabels[r.type]);
        existing.relationshipsId?.push(r.relationshipId);
      } else {
        acc.push({
          label: r.individualDataOverride?.fullName ?? r.entityDataOverride?.name ?? '',
          value: false,
          type: RelationshipTypeLabels[r.type],
          types: [RelationshipTypeLabels[r.type]],
          address: r.individualDataOverride?.address?.normalizedFullAddress
            ?? r.entityDataOverride?.registeredAddress?.normalizedFullAddress
            ?? '',
          relationshipsId: [r.relationshipId],
          relationship: r,
          denyMinutesDocumentType
        });
      }
      return acc;
    }, [] as BulkMatches[])?.sort((a, b) => {
      // Priority order: Director first, then Shareholder, then alphabetical
      const getPriority = (types: string[]) => {
        if (types.includes('Shareholder')) return 0;
        if (types.includes('Director')) return 1;
        return 2;
      };

      const aPriority = getPriority(a.types ?? []);
      const bPriority = getPriority(b.types ?? []);

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return a.label.localeCompare(b.label);
    }) ?? [];

    relationShips.forEach(element => {
      if ((element.types ?? []).length > 1) {
        element.type = [...new Set(element.types)].join(', ');
      }
    });

    return [
      ...transformRegisteredAddress,
      ...transformPrincipalAddress,
      ...relationShips
    ];
  };

  const transformData: BulkChangeData[] = [
    ...res.companies.map(c => transformEntity(c, 'acn')),
    ...res.trusts.map(c => transformEntity(c, 'abn'))
  ];

  return transformData;
}
